/**
 * TaskTypesPage - Admin UI for TaskCategory and TaskSubCategory management
 * EF-TASK-TYPE-ADMIN-01: Task Type Administration layer
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  FolderTree,
  ChevronRight,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  Lock,
  AlertTriangle,
  Hash,
  Link as LinkIcon,
  FileText,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { cn } from "../lib/utils";
import { PageHeader, LoadingSpinner, EmptyState } from "../components/shared/DataComponents";
import {
  getTaskCategories,
  createTaskCategory,
  updateTaskCategory,
  deleteTaskCategory,
  getTaskCategoryUsage,
  getTaskSubCategories,
  createTaskSubCategory,
  updateTaskSubCategory,
  deleteTaskSubCategory,
  getTaskSubCategoryUsage,
  extractErrorMessage,
} from "../lib/api";

export default function TaskTypesPage() {
  // State
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [subCategoryDialogOpen, setSubCategoryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'category'|'subcategory', item: {...} }
  const [usageInfo, setUsageInfo] = useState(null);
  
  // Form state
  const [categoryForm, setCategoryForm] = useState({
    key: "",
    name: "",
    description: "",
    sort_order: 0,
    is_active: true,
  });
  const [subCategoryForm, setSubCategoryForm] = useState({
    key: "",
    name: "",
    description: "",
    category_id: "",
    sort_order: 0,
    is_active: true,
    tax_rate: "",
    financial_group: "",
  });

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, subCatRes] = await Promise.all([
        getTaskCategories(),
        getTaskSubCategories(),
      ]);
      setCategories(catRes.data);
      setSubCategories(subCatRes.data);
    } catch (err) {
      toast.error(`Failed to load data: ${extractErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Group subcategories by category
  const subCategoriesByCategory = useMemo(() => {
    const map = {};
    subCategories.forEach(sc => {
      if (!map[sc.category_id]) map[sc.category_id] = [];
      map[sc.category_id].push(sc);
    });
    // Sort by sort_order
    Object.values(map).forEach(arr => arr.sort((a, b) => a.sort_order - b.sort_order));
    return map;
  }, [subCategories]);

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase();
    return categories.filter(cat => {
      const catMatch = cat.name.toLowerCase().includes(query) || cat.key.toLowerCase().includes(query);
      const subs = subCategoriesByCategory[cat.id] || [];
      const subMatch = subs.some(sc => sc.name.toLowerCase().includes(query) || sc.key.toLowerCase().includes(query));
      return catMatch || subMatch;
    });
  }, [categories, searchQuery, subCategoriesByCategory]);

  // Toggle expand/collapse
  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Expand all
  const expandAll = () => {
    setExpandedCategories(new Set(categories.map(c => c.id)));
  };

  // Collapse all
  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  // Open category dialog
  const openCategoryDialog = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryForm({
        key: category.key,
        name: category.name,
        description: category.description || "",
        sort_order: category.sort_order || 0,
        is_active: category.is_active,
      });
    } else {
      setSelectedCategory(null);
      setCategoryForm({
        key: "",
        name: "",
        description: "",
        sort_order: 0,
        is_active: true,
      });
    }
    setCategoryDialogOpen(true);
  };

  // Open subcategory dialog
  const openSubCategoryDialog = (categoryId, subCategory = null) => {
    if (subCategory) {
      setSelectedSubCategory(subCategory);
      setSubCategoryForm({
        key: subCategory.key,
        name: subCategory.name,
        description: subCategory.description || "",
        category_id: subCategory.category_id,
        sort_order: subCategory.sort_order || 0,
        is_active: subCategory.is_active,
        tax_rate: subCategory.tax_rate?.toString() || "",
        financial_group: subCategory.financial_group || "",
      });
    } else {
      setSelectedSubCategory(null);
      setSubCategoryForm({
        key: "",
        name: "",
        description: "",
        category_id: categoryId,
        sort_order: 0,
        is_active: true,
        tax_rate: "",
        financial_group: "",
      });
    }
    setSubCategoryDialogOpen(true);
  };

  // Handle category submit
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...categoryForm,
        key: categoryForm.key.toUpperCase().trim(),
      };
      if (selectedCategory) {
        // Update - key is immutable for system-defined
        const updateData = { ...data };
        if (selectedCategory.system_defined || selectedCategory.immutable) {
          delete updateData.key;
        }
        await updateTaskCategory(selectedCategory.id, updateData);
        toast.success("Category updated");
      } else {
        await createTaskCategory(data);
        toast.success("Category created");
      }
      setCategoryDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  // Handle subcategory submit
  const handleSubCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...subCategoryForm,
        key: subCategoryForm.key.toUpperCase().trim(),
        tax_rate: subCategoryForm.tax_rate ? parseFloat(subCategoryForm.tax_rate) : null,
      };
      if (selectedSubCategory) {
        // Update - key is immutable for system-defined
        const updateData = { ...data };
        if (selectedSubCategory.system_defined || selectedSubCategory.immutable) {
          delete updateData.key;
          delete updateData.category_id;
        }
        await updateTaskSubCategory(selectedSubCategory.id, updateData);
        toast.success("SubCategory updated");
      } else {
        await createTaskSubCategory(data);
        toast.success("SubCategory created");
      }
      setSubCategoryDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  // Open delete confirmation
  const openDeleteDialog = async (type, item) => {
    setDeleteTarget({ type, item });
    setUsageInfo(null);
    
    try {
      if (type === 'category') {
        const res = await getTaskCategoryUsage(item.id);
        setUsageInfo(res.data);
      } else {
        const res = await getTaskSubCategoryUsage(item.id);
        setUsageInfo(res.data);
      }
    } catch (err) {
      console.error("Failed to get usage info:", err);
    }
    
    setDeleteDialogOpen(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (deleteTarget.type === 'category') {
        await deleteTaskCategory(deleteTarget.item.id);
        toast.success(`Category "${deleteTarget.item.name}" deleted`);
      } else {
        await deleteTaskSubCategory(deleteTarget.item.id);
        toast.success(`SubCategory "${deleteTarget.item.name}" deleted`);
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  // Get task definition count for a category
  const getTaskDefCount = useCallback((categoryId, subcategoryId = null) => {
    // This would need to be fetched from backend
    // For now, we show it in the usage dialog
    return null;
  }, []);

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Task Types"
        description="Manage task categories and subcategories for classification and rule filtering"
        icon={FolderTree}
        actions={
          <Button
            onClick={() => openCategoryDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
            data-testid="add-category-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        }
      />

      {/* Search and controls */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search categories or subcategories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandAll} data-testid="expand-all-btn">
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} data-testid="collapse-all-btn">
            Collapse All
          </Button>
          <Button variant="ghost" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Categories Table */}
      <div className="bg-card border border-white/5 rounded-sm">
        {filteredCategories.length === 0 ? (
          <EmptyState
            icon={FolderTree}
            title="No task types found"
            description={searchQuery ? `No results for "${searchQuery}"` : "Create your first category to get started"}
            action={
              !searchQuery && (
                <Button onClick={() => openCategoryDialog()} className="bg-primary text-primary-foreground">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              )
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-white/5">
                <TableHead className="w-10"></TableHead>
                <TableHead className="w-32">Key</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-24 text-center">SubCats</TableHead>
                <TableHead className="w-24 text-center">Status</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCategories.sort((a, b) => a.sort_order - b.sort_order).map((category) => {
                const subs = subCategoriesByCategory[category.id] || [];
                const isExpanded = expandedCategories.has(category.id);
                const isSystem = category.system_defined || category.immutable;
                
                return (
                  <>
                    {/* Category Row */}
                    <TableRow
                      key={category.id}
                      className="hover:bg-surface-elevated/50 border-white/5 cursor-pointer"
                      onClick={() => subs.length > 0 && toggleExpand(category.id)}
                      data-testid={`category-row-${category.id}`}
                    >
                      <TableCell className="w-10">
                        {subs.length > 0 ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleExpand(category.id); }}
                            className="p-1 hover:bg-white/5 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        ) : (
                          <span className="w-6" />
                        )}
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-surface-elevated px-2 py-1 rounded font-mono">
                          {category.key}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{category.name}</span>
                          {isSystem && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Lock className="h-3 w-3 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>System-defined (immutable)</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        {category.description && (
                          <p className="text-xs text-muted-foreground mt-1">{category.description}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-xs">
                          {subs.length}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={category.is_active ? "default" : "secondary"}
                          className={cn(
                            "text-xs",
                            category.is_active ? "bg-green-600/20 text-green-400 border-green-600/30" : ""
                          )}
                        >
                          {category.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => openSubCategoryDialog(category.id)}
                                  data-testid={`add-subcategory-${category.id}`}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Add SubCategory</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => openCategoryDialog(category)}
                                  data-testid={`edit-category-${category.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit Category</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {!isSystem && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 hover:text-destructive"
                                    onClick={() => openDeleteDialog('category', category)}
                                    data-testid={`delete-category-${category.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete Category</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* SubCategory Rows */}
                    {isExpanded && subs.map((subCat) => {
                      const isSubSystem = subCat.system_defined || subCat.immutable;
                      
                      return (
                        <TableRow
                          key={subCat.id}
                          className="hover:bg-surface-elevated/30 border-white/5 bg-black/20"
                          data-testid={`subcategory-row-${subCat.id}`}
                        >
                          <TableCell></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 pl-4">
                              <span className="text-muted-foreground">└</span>
                              <code className="text-xs bg-surface-elevated px-2 py-1 rounded font-mono">
                                {subCat.key}
                              </code>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{subCat.name}</span>
                              {isSubSystem && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Lock className="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>System-defined (immutable)</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {subCat.is_travel_related && (
                                <Badge variant="outline" className="text-xs">Travel</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {subCat.tax_rate != null && (
                              <span className="text-xs text-muted-foreground">{subCat.tax_rate}%</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={subCat.is_active ? "default" : "secondary"}
                              className={cn(
                                "text-xs",
                                subCat.is_active ? "bg-green-600/20 text-green-400 border-green-600/30" : ""
                              )}
                            >
                              {subCat.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => openSubCategoryDialog(category.id, subCat)}
                                      data-testid={`edit-subcategory-${subCat.id}`}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit SubCategory</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {!isSubSystem && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 hover:text-destructive"
                                        onClick={() => openDeleteDialog('subcategory', subCat)}
                                        data-testid={`delete-subcategory-${subCat.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete SubCategory</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedCategory ? "Edit Category" : "Add Category"}
            </DialogTitle>
            <DialogDescription>
              Categories group related task subcategories together
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div>
              <Label htmlFor="cat-key">Key *</Label>
              <Input
                id="cat-key"
                value={categoryForm.key}
                onChange={(e) => setCategoryForm({ ...categoryForm, key: e.target.value.toUpperCase() })}
                placeholder="e.g., EQUIPMENT"
                disabled={selectedCategory?.system_defined || selectedCategory?.immutable}
                required
                data-testid="category-key-input"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Unique uppercase identifier</p>
            </div>
            <div>
              <Label htmlFor="cat-name">Name *</Label>
              <Input
                id="cat-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Equipment"
                required
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label htmlFor="cat-desc">Description</Label>
              <Textarea
                id="cat-desc"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
                data-testid="category-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cat-sort">Sort Order</Label>
                <Input
                  id="cat-sort"
                  type="number"
                  value={categoryForm.sort_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) || 0 })}
                  data-testid="category-sort-input"
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  id="cat-active"
                  checked={categoryForm.is_active}
                  onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, is_active: checked })}
                  data-testid="category-active-switch"
                />
                <Label htmlFor="cat-active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground" data-testid="category-submit-btn">
                {selectedCategory ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SubCategory Dialog */}
      <Dialog open={subCategoryDialogOpen} onOpenChange={setSubCategoryDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedSubCategory ? "Edit SubCategory" : "Add SubCategory"}
            </DialogTitle>
            <DialogDescription>
              SubCategories define specific task types within a category
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubCategorySubmit} className="space-y-4">
            <div>
              <Label htmlFor="sub-key">Key *</Label>
              <Input
                id="sub-key"
                value={subCategoryForm.key}
                onChange={(e) => setSubCategoryForm({ ...subCategoryForm, key: e.target.value.toUpperCase() })}
                placeholder="e.g., LIGHTING"
                disabled={selectedSubCategory?.system_defined || selectedSubCategory?.immutable}
                required
                data-testid="subcategory-key-input"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Unique uppercase identifier within category</p>
            </div>
            <div>
              <Label htmlFor="sub-name">Name *</Label>
              <Input
                id="sub-name"
                value={subCategoryForm.name}
                onChange={(e) => setSubCategoryForm({ ...subCategoryForm, name: e.target.value })}
                placeholder="e.g., Lighting Equipment"
                required
                data-testid="subcategory-name-input"
              />
            </div>
            <div>
              <Label htmlFor="sub-desc">Description</Label>
              <Textarea
                id="sub-desc"
                value={subCategoryForm.description}
                onChange={(e) => setSubCategoryForm({ ...subCategoryForm, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
                data-testid="subcategory-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sub-tax">Default Tax Rate (%)</Label>
                <Input
                  id="sub-tax"
                  type="number"
                  step="0.01"
                  value={subCategoryForm.tax_rate}
                  onChange={(e) => setSubCategoryForm({ ...subCategoryForm, tax_rate: e.target.value })}
                  placeholder="e.g., 20"
                  data-testid="subcategory-tax-input"
                />
              </div>
              <div>
                <Label htmlFor="sub-group">Financial Group</Label>
                <Input
                  id="sub-group"
                  value={subCategoryForm.financial_group}
                  onChange={(e) => setSubCategoryForm({ ...subCategoryForm, financial_group: e.target.value })}
                  placeholder="e.g., EXPENSES"
                  data-testid="subcategory-group-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sub-sort">Sort Order</Label>
                <Input
                  id="sub-sort"
                  type="number"
                  value={subCategoryForm.sort_order}
                  onChange={(e) => setSubCategoryForm({ ...subCategoryForm, sort_order: parseInt(e.target.value) || 0 })}
                  data-testid="subcategory-sort-input"
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  id="sub-active"
                  checked={subCategoryForm.is_active}
                  onCheckedChange={(checked) => setSubCategoryForm({ ...subCategoryForm, is_active: checked })}
                  data-testid="subcategory-active-switch"
                />
                <Label htmlFor="sub-active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSubCategoryDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground" data-testid="subcategory-submit-btn">
                {selectedSubCategory ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {usageInfo && !usageInfo.can_delete ? (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              ) : (
                <Trash2 className="h-5 w-5 text-destructive" />
              )}
              Delete {deleteTarget?.type === 'category' ? 'Category' : 'SubCategory'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{deleteTarget?.item?.name}</strong>?
              </p>
              
              {usageInfo && (
                <div className="bg-surface-elevated p-3 rounded-lg space-y-2 text-sm">
                  <p className="font-medium text-foreground">Usage Information:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span>Task Definitions: {usageInfo.task_definition_count}</span>
                    </div>
                    {deleteTarget?.type === 'category' && (
                      <div className="flex items-center gap-2">
                        <FolderTree className="h-4 w-4 text-muted-foreground" />
                        <span>SubCategories: {usageInfo.subcategory_count}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <LinkIcon className="h-4 w-4 text-muted-foreground" />
                      <span>Rule Associations: {usageInfo.rule_association_count}</span>
                    </div>
                  </div>
                  
                  {!usageInfo.can_delete && usageInfo.delete_blocked_reason && (
                    <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-amber-400">
                      <p className="font-medium">Deletion Blocked</p>
                      <p className="text-xs">{usageInfo.delete_blocked_reason}</p>
                    </div>
                  )}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={usageInfo && !usageInfo.can_delete}
              className={cn(
                usageInfo?.can_delete 
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" 
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              )}
              data-testid="confirm-delete-btn"
            >
              {usageInfo?.can_delete ? "Delete" : "Cannot Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
